import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ClientType } from 'app/order-management/components/order-history/order-history.component';
import { DownloadsService } from 'app/content/services/downloads.service';
import { ApiModel } from 'models';
import { LanguageService, SearchParameters } from 'services';
import { FacetsUiMapper, OrderHistoryFacet, OrderHistoryFacets } from 'services/product-search/facets-ui-mapper';
import { RouterHandler } from 'services/product-search/router-handler';
import { Searcher } from './searcher';
import { HistoricalOrder, HistoricalOrderOverview } from './searcher.types';

@Injectable()
export class OrderHistoryService {
    private searcher: Searcher;
    private routerHandler: RouterHandler;
    private facetsUiMapper: FacetsUiMapper;
    private apiModel: ApiModel;

    constructor(
        apiModel: ApiModel,
        router: Router,
        languageService: LanguageService,
        private downloadsService: DownloadsService,
        private activatedRoute: ActivatedRoute
    ) {
        this.apiModel = apiModel;
        this.searcher = new Searcher(apiModel);
        this.routerHandler = new RouterHandler(router, this.activatedRoute);
        this.facetsUiMapper = new FacetsUiMapper(languageService);
    }

    public getOrders(searchParameters: SearchParameters): Observable<{
        orders: HistoricalOrderOverview[];
        nextId: string;
        facets: OrderHistoryFacets;
    }> {
        return this.searcher.getOrders(searchParameters);
    }

    public getOrder(orderId: string, type?: string): Observable<HistoricalOrder> {
        return this.searcher.getOrder(orderId, type);
    }

    public mapUrlParams(paramMap: ParamMap): SearchParameters {
        return this.routerHandler.mapUrlParams(paramMap);
    }

    public navigate({ query, pagination, filters, sortingCriteria }: Partial<SearchParameters>) {
        return this.routerHandler.navigate({ query, pagination, filters, sortingCriteria });
    }

    public mapFacetToRetailersFilter(retailersFacet: OrderHistoryFacet[], selectedRetailers: string[]) {
        return this.facetsUiMapper.mapFacetToBrandsAndRetailersFilter(retailersFacet, selectedRetailers);
    }

    public mapFacetToRetailersFilterV2(retailersFacet: OrderHistoryFacet[], selectedRetailers: string[]) {
        return this.facetsUiMapper.mapFacetToBrandsAndRetailersFilterV2(retailersFacet, selectedRetailers);
    }

    public mapFacetToBrandsFilter(brandsFacet: OrderHistoryFacet[], selectedBrands: string[]) {
        return this.facetsUiMapper.mapFacetToBrandsAndRetailersFilter(brandsFacet, selectedBrands);
    }

    public mapFacetToBrandsFilterV2(brandsFacet: OrderHistoryFacet[], selectedBrands: string[]) {
        return this.facetsUiMapper.mapFacetToBrandsAndRetailersFilterV2(brandsFacet, selectedBrands);
    }

    public getOrderTypeName(clientType: ClientType) {
        return this.facetsUiMapper.getOrderTypeName(clientType);
    }

    public mapFacetToOrderTypesFilter(orderTypesFacet: OrderHistoryFacet[], selectedOrderTypes: string[]) {
        return this.facetsUiMapper.mapFacetToOrderTypesFilter(orderTypesFacet, selectedOrderTypes);
    }

    public mapFacetToOrderDatesFilter(orderDatesFacet: OrderHistoryFacet[], selectedOrderDate: string) {
        return this.facetsUiMapper.mapFacetToOrderDatesFilter(orderDatesFacet, selectedOrderDate);
    }

    public downloadOrderDetailsPDF(payload: DownloadOrderOverviewRequest) {
        return this.apiModel
            .makeHttpObservableBlobRequest({
                method: 'post',
                url: `${this.apiModel.getOrderEndpoint()}/pdf`,
                headers: new HttpHeaders({ Accept: 'application/pdf' }),
                body: payload
            })
            .pipe(tap((res) => this.downloadsService.handleFileResponse(res, 'order_details.pdf')));
    }

    public downloadOrderDetailsCSV(payload: DownloadOrderOverviewRequest) {
        return this.apiModel
            .makeHttpObservableBlobRequest({
                method: 'post',
                url: `${this.apiModel.getOrderEndpoint()}/csv`,
                headers: new HttpHeaders({ Accept: 'application/pdf' }),
                body: payload
            })
            .pipe(tap((res) => this.downloadsService.handleFileResponse(res, 'order_details.csv')));
    }
}

export type DownloadOrderOverviewRequest = {
    order: {
        orderNumber: string;
        type: string;
    };
    includeImages: boolean;
    locale: string;
};
